<script setup lang="ts">
import FeatureGateNotice from '@/components/subscription/FeatureGateNotice.vue';
import RecurringTaskConfig from '@/components/tasks/RecurringTaskConfig.vue';
import { DatePicker } from '@/components/ui/date-picker';
import { Switch } from '@/components/ui/switch';
import { Repeat2 } from 'lucide-vue-next';
import type { Task } from '~/types/models';

defineProps<{
  scheduleSummary: string;
  reminderSummary: string;
  recurringSummary: string;
  reminderDate: string;
  recurring?: Task['recurring'];
  canUseRecurringTasks: boolean;
  recurringRequirement: string;
}>();

const emit = defineEmits<{
  (event: 'update:reminder', value: string): void;
  (event: 'toggle:recurring', value: boolean): void;
  (event: 'update:recurring', value: NonNullable<Task['recurring']>): void;
}>();
</script>

<template>
  <section>
    <div class="mb-3">
      <p class="text-base font-semibold text-foreground">Schedule</p>
      <p class="text-sm text-muted-foreground">Manage reminders and repeat rules here. Dates can still be adjusted from the header above.</p>
    </div>

    <div class="divide-y divide-border/60 border-y border-border/70">
      <div class="grid gap-4 py-4 md:grid-cols-3">
        <div><p class="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Timeline</p><p class="mt-2 text-sm font-medium">{{ scheduleSummary }}</p></div>
        <div><p class="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Reminder</p><p class="mt-2 text-sm font-medium">{{ reminderSummary }}</p></div>
        <div><p class="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Recurring</p><p class="mt-2 text-sm font-medium">{{ recurringSummary }}</p></div>
      </div>

      <div class="py-5">
        <label class="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/85">Reminder</label>
        <DatePicker
          :model-value="reminderDate"
          placeholder="Select reminder date"
          :show-icon="false"
          class="mt-3 bg-background/84"
          @update:model-value="value => emit('update:reminder', value)"
        />
      </div>

      <div class="py-5">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="inline-flex items-center gap-2">
              <Repeat2 class="size-3.5 text-muted-foreground/75" />
              <p class="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground/80">Recurring</p>
            </div>
            <p class="mt-2 text-sm font-medium text-foreground">Make this task recurring</p>
            <p class="mt-1 text-xs text-muted-foreground">{{ recurringSummary }}</p>
          </div>
          <Switch :checked="recurring?.enabled === true" @update:checked="value => emit('toggle:recurring', value)" />
        </div>

        <div v-if="recurring?.enabled" class="mt-4 border-t border-border/60 pt-4">
          <FeatureGateNotice
            v-if="!canUseRecurringTasks"
            compact
            title="Recurring tasks unlock on Growth"
            description="Upgrade when you want repeatable client work, retainers, and recurring delivery rhythms generated from the task itself."
            :requirement="recurringRequirement"
          />
          <RecurringTaskConfig
            v-else
            :model-value="recurring"
            hide-header
            :show-summary-footer="false"
            @update:model-value="value => emit('update:recurring', value)"
          />
        </div>
      </div>
    </div>
  </section>
</template>
